import React from "react";
import { useTranslation } from "react-i18next";
import type { UserModel3DCommerceFormState } from "../../features/floorplan3d/userModels/userModel3DCommerce";
import {
  Currency,
  getCurrenciesWithPreferredFirst,
  getCurrencyDisplaySymbol,
} from "../../utils/currency";

type Props = {
  value: UserModel3DCommerceFormState;
  onChange: (next: UserModel3DCommerceFormState) => void;
  disabled?: boolean;
  /** Compact layout for the upload confirm strip in the library panel. */
  compact?: boolean;
};

/**
 * Optional price / currency / store URL / brand — mirrors Personal Library fields.
 * Currency defaults to the project picker value; the user can override per object.
 */
export const UserModel3DCommerceFields: React.FC<Props> = ({
  value,
  onChange,
  disabled = false,
  compact = false,
}) => {
  const { t } = useTranslation();
  const currencies = React.useMemo(() => getCurrenciesWithPreferredFirst(), []);
  const inputClass = compact
    ? "mt-1 h-9 w-full rounded border border-[#c9c6be] bg-white px-2 text-xs text-[#111] outline-none focus:border-[#111] focus:ring-1 focus:ring-[#111] disabled:opacity-60"
    : "mt-1.5 h-10 w-full rounded-lg border border-[#c9c6be] bg-white px-3 text-sm text-[#111] outline-none focus:border-[#111] focus:ring-1 focus:ring-[#111] disabled:opacity-60";
  const labelClass = compact
    ? "text-[11px] font-semibold uppercase text-gray-600"
    : "text-sm font-medium text-[#272622]";

  return (
    <fieldset
      className={
        compact
          ? "mt-3 space-y-2 border-t border-[#dfe5e2] pt-3"
          : "space-y-2"
      }
    >
      <legend
        className={
          compact ? "sr-only" : "text-sm font-medium text-[#272622]"
        }
      >
        {t("userModels3d.commerceLegend")}
      </legend>
      {!compact && (
        <p className="text-[11px] leading-snug text-[#625f58]">
          {t("userModels3d.commerceHint")}
        </p>
      )}
      <div className={compact ? "grid grid-cols-1 gap-2" : "space-y-3"}>
        <div
          className={
            compact
              ? "grid grid-cols-2 gap-2"
              : "grid grid-cols-1 gap-3 sm:grid-cols-2"
          }
        >
          <label className="block min-w-0">
            <span className={labelClass}>{t("userModels3d.priceInclVat")}</span>
            <input
              type="number"
              inputMode="decimal"
              min={0}
              step="any"
              value={value.price}
              disabled={disabled}
              placeholder={t("userModels3d.pricePlaceholder")}
              onChange={(event) =>
                onChange({ ...value, price: event.target.value })
              }
              className={inputClass}
            />
          </label>
          <label className="block min-w-0">
            <span className={labelClass}>{t("userModels3d.currency")}</span>
            <select
              value={value.currency}
              disabled={disabled}
              aria-label={t("userModels3d.currency")}
              onChange={(event) =>
                onChange({
                  ...value,
                  currency: event.target.value as Currency,
                })
              }
              className={inputClass}
            >
              {currencies.map((code) => (
                <option key={code} value={code}>
                  {code}
                  {getCurrencyDisplaySymbol(code) !== code
                    ? ` (${getCurrencyDisplaySymbol(code)})`
                    : ""}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="block">
          <span className={labelClass}>{t("userModels3d.brand")}</span>
          <input
            type="text"
            value={value.brand}
            disabled={disabled}
            maxLength={120}
            placeholder={t("userModels3d.brandPlaceholder")}
            onChange={(event) =>
              onChange({ ...value, brand: event.target.value })
            }
            className={inputClass}
          />
        </label>
        <label className="block">
          <span className={labelClass}>{t("userModels3d.storeUrl")}</span>
          <input
            type="url"
            value={value.url}
            disabled={disabled}
            placeholder={t("userModels3d.storeUrlPlaceholder")}
            onChange={(event) =>
              onChange({ ...value, url: event.target.value })
            }
            className={inputClass}
          />
        </label>
      </div>
    </fieldset>
  );
};
